import React, { useState } from "react";
import { ConfirmationDialog } from "ui/confirmation-dialog";

import { getTouchpointVersion } from "generated/api/touchpoint-version";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";

import toast from "components/partials/toast/toast";

import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";
import { TouchpointStatuses } from "types";

interface RejectAsClientModalProps {
  setIsOpen: (isOpen: boolean) => void;
  selectedVersion?: TouchpointVersionAttributes;
  setSelectedVersion: (version: TouchpointVersionAttributes) => void;
}

export const RejectAsClientModal = ({
  setIsOpen,
  selectedVersion,
  setSelectedVersion,
}: RejectAsClientModalProps) => {
  const currentClient = useCurrentClient();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const { refetch: refetchTouchpointVersions } = useTouchpointVersions({
    clientId: currentClient.id,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });
  const [isLoading, setIsLoading] = useState(false);

  const rejectTouchpointAsClient = async (): Promise<void> => {
    try {
      if (
        !currentClient?.id ||
        !currentCampaign?.id ||
        !currentTouchpoint?.id ||
        !selectedVersion?.id
      ) {
        throw new Error("Missing required information to reject touchpoint.");
      }

      setIsLoading(true);

      const touchpointVersion = getTouchpointVersion();

      await touchpointVersion.patchTouchpointVersion(
        {
          clientId: currentClient.id,
          campaignId: currentCampaign.id,
          touchpointId: currentTouchpoint.id,
          id: selectedVersion.id,
        },
        { status: TouchpointStatuses.REJECTED },
      );

      const versionsResult = await refetchTouchpointVersions();
      const items = versionsResult.data?.items ?? [];

      const newSystemDraft = items.find((v) => v.status === TouchpointStatuses.SYSTEM_DRAFT);

      if (newSystemDraft) {
        setSelectedVersion(newSystemDraft);
      }

      setIsOpen(false);
      toast.success({ data: { title: "Rejected creative version" } });
    } catch (err) {
      console.error("Error in rejectTouchpointAsClient:", err);
      toast.error({
        data: {
          title: "Failed to reject creative",
        },
        autoClose: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationDialog
      open
      intent="warning"
      onClose={() => setIsOpen(false)}
      onConfirm={rejectTouchpointAsClient}
      headline="Are you sure you want to reject as client?"
      confirmButtonText="Reject as client"
      confirming={isLoading}
    />
  );
};
